import { Button, Checkbox, Dialog, DialogActions, DialogContent, FormControlLabel } from "@mui/material";
import { sortByKey } from "@notemeal/utils-sort";
import { useState } from "react";
import DialogTitle from "../../../componentLibrary/DialogTitle";
import { useSnackbar } from "../../../components/Snackbar/SnackbarContext";
import LoadingBackdrop from "../../../components/universal/LoadingBackdrop";
import { useEnvironmentType } from "../../../contexts/useEnvType";
import { FeatureFlagsFragment, useEditOrgFeatureFlagsMutation } from "../../../types";

interface OrgFeatureFlagsDialogProps {
  open: boolean;
  onClose: () => void;
  orgId: string;
  featureFlags: FeatureFlagsFragment;
  hasTeamworksIdWithSuperuser: boolean;
}

const TACTICAL_ONLY_FLAGS: FlagLabelInfo[] = [
  { flag: "twUserManagement", displayName: "Teamworks User Management (TUM)" },
  { flag: "bulkDeleteMealPlans", displayName: "Bulk Delete Meal Plans" },
  { flag: "umaNotifications", displayName: "UMA Notifications" },
  { flag: "appSwitcher", displayName: "App Switcher" },
  { flag: "axleEvents", displayName: "Axle Events" },
  { flag: "amsDashboard", displayName: "AMS Dashboard" },
  { flag: "showAxleUpsertTab", displayName: "Show Axle Upsert Tab" },
  { flag: "showDeleteAthleteButton", displayName: "Show Delete Athlete Button" },
  { flag: "isUnityMessagingEnabled", displayName: "Unity Messaging" },
  { flag: "isEnergyExpenditureEnabled", displayName: "Energy Expenditure" },
  { flag: "isMenuBuilderMultiWeekCopyingEnabled", displayName: "Menu Builder Multi Week Copying" },
  { flag: "isAiFoodLoggingEnabled", displayName: "AI Food Logging" },
  { flag: "teamScopedPermissions", displayName: "Team Scoped Permissions" },
];

export const OrgFeatureFlagsDialog = ({ open, onClose, orgId, featureFlags, hasTeamworksIdWithSuperuser }: OrgFeatureFlagsDialogProps) => {
  const [state, setState] = useState<FeatureFlagsFragment>(featureFlags);
  const { isTactical } = useEnvironmentType();
  const { setMessage } = useSnackbar();
  const [editOrgFeatureFlags, { loading }] = useEditOrgFeatureFlagsMutation({
    onCompleted: onClose,
    onError: () => {
      setMessage("error", "Something went wrong, operation failed.");
      onClose();
    },
    update: (cache, { data }) => {
      if (data) {
        cache.modify({
          id: `Org:${orgId}`,
          fields: {
            featureFlags: () => data?.editOrgFeatureFlags.featureFlags,
          },
        });
      }
    },
  });

  const handleSave = () => {
    editOrgFeatureFlags({
      variables: {
        input: {
          orgId,
          featureFlags: {
            athleteMenuSuggestions: state.athleteMenuSuggestions,
            clientCredentials: state.clientCredentials,
            hasRestaurantMenus: state.hasRestaurantMenus,
            restaurantLogging: state.restaurantLogging,
            foodCategory: state.foodCategory,
            orgGroupRecipes: state.orgGroupRecipes,
            orgGroupFoods: state.orgGroupFoods,
            performanceKitchen: state.performanceKitchen,
            printableTags: state.printableTags,
            hubMenuCheckIn: state.hubMenuCheckIn,
            dietitianOnlyMenus: state.dietitianOnlyMenus,
            bulkOrdering: state.bulkOrdering,
            ukMealPlanOptionSuggestions: state.ukMealPlanOptionSuggestions,
            digitalDisplays: state.digitalDisplays,
            selfServiceOnboarding: state.selfServiceOnboarding,
            messaging: state.messaging,
            eatingDisorderScreens: state.eatingDisorderScreens,
            profilesV3Api: state.profilesV3Api,
            educationResources: state.educationResources,
            diningOptionInMenu: state.diningOptionInMenu,
            dietitianSupportForms: state.dietitianSupportForms,
            mavenSupportChat: state.mavenSupportChat,
            twUserManagement: state.twUserManagement,
            bulkDeleteMealPlans: state.bulkDeleteMealPlans,
            umaNotifications: state.umaNotifications,
            appSwitcher: state.appSwitcher,
            axleEvents: state.axleEvents,
            amsDashboard: state.amsDashboard,
            showAxleUpsertTab: state.showAxleUpsertTab,
            showDeleteAthleteButton: state.showDeleteAthleteButton,
            isUnityMessagingEnabled: state.isUnityMessagingEnabled,
            isEnergyExpenditureEnabled: state.isEnergyExpenditureEnabled,
            isMenuBuilderMultiWeekCopyingEnabled: state.isMenuBuilderMultiWeekCopyingEnabled,
            isAiFoodLoggingEnabled: state.isAiFoodLoggingEnabled,
            teamScopedPermissions: state.teamScopedPermissions,
          },
        },
      },
    });
  };

  if (loading) {
    return <LoadingBackdrop open={open} onClose={onClose} />;
  }

  const flagInfoList: FlagLabelInfo[] = [
    { flag: "athleteMenuSuggestions", displayName: "Athlete Menu Suggestions" },
    { flag: "clientCredentials", displayName: "Client Credentials" },
    { flag: "hasRestaurantMenus", displayName: "Catering Restaurant Menus" },
    { flag: "restaurantLogging", displayName: "Restaurant Logging" },
    { flag: "foodCategory", displayName: "Food Category" },
    { flag: "orgGroupRecipes", displayName: "Org Group Recipes" },
    { flag: "orgGroupFoods", displayName: "Org Group Foods" },
    { flag: "performanceKitchen", displayName: "Performance Kitchen" },
    { flag: "printableTags", displayName: "Printable Tags" },
    { flag: "dietitianOnlyMenus", displayName: "Dietitian Only Menus" },
    { flag: "bulkOrdering", displayName: "Dietitian Bulk Ordering" },
    { flag: "ukMealPlanOptionSuggestions", displayName: "UK Meal Plan Option Suggestions" },
    { flag: "digitalDisplays", displayName: "Digital Displays Export" },
    { flag: "selfServiceOnboarding", displayName: "User Self Service Onboarding" },
    { flag: "messaging", displayName: "Messaging" },
    { flag: "eatingDisorderScreens", displayName: "Eating Disorder Screen For Athletes" },
    { flag: "profilesV3Api", displayName: "Profiles V3 API" },
    { flag: "educationResources", displayName: "Education Resources" },
    { flag: "diningOptionInMenu", displayName: "Dining Option" },
    { flag: "dietitianSupportForms", displayName: "Intake Forms" },
    { flag: "mavenSupportChat", displayName: "Maven Chat" },
  ];

  if (isTactical) {
    flagInfoList.push(...TACTICAL_ONLY_FLAGS);
  }

  if (hasTeamworksIdWithSuperuser) {
    flagInfoList.push({ flag: "hubMenuCheckIn", displayName: "Hub Kiosk Menu Checkin" });
  }

  const sortedFlagsInfoList = sortByKey(flagInfoList, "displayName");

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle title="Edit Feature Flags" onClose={onClose} />
      <DialogContent sx={{ display: "flex", flexDirection: "column" }}>
        {sortedFlagsInfoList.map(flagInfo => (
          <FeatureFlagFormControlLabel key={flagInfo.flag} flags={state} onChangeFlags={setState} {...flagInfo} />
        ))}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={handleSave}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

interface FlagLabelInfo {
  flag: keyof FeatureFlagsFragment;
  displayName: string;
}

interface FeatureFlagFormControlLabelProps extends FlagLabelInfo {
  flags: FeatureFlagsFragment;
  onChangeFlags: (value: FeatureFlagsFragment) => void;
}

const FeatureFlagFormControlLabel = ({ flags, onChangeFlags, flag, displayName }: FeatureFlagFormControlLabelProps) => {
  return (
    <FormControlLabel
      control={<Checkbox checked={flags[flag]} onChange={(_, v) => onChangeFlags({ ...flags, [flag]: v })} />}
      label={displayName}
    />
  );
};
